import { skipToken, useApolloClient, useSuspenseQuery } from "@apollo/client/react";
import { useCallback, useMemo } from "react";
import {
  buildEffectiveRecordSchemasQuery,
  type EffectiveRecordPropertySchemasByCategory,
  readEffectiveRecordSchemas,
} from "~/features/recordDetails/utils/effective-property-schemas";
import { useTenant } from "~/features/tenant/context";

export function useEffectiveRecordPropertySchemas(categoryIDs: readonly string[]) {
  const { tenantId } = useTenant();
  const client = useApolloClient();
  if (!tenantId) throw new Error("Tenant ID is required");
  const categoryKey = [...new Set(categoryIDs)].sort().join(",");
  const ids = useMemo(() => (categoryKey ? categoryKey.split(",") : []), [categoryKey]);
  const query = useMemo(() => buildEffectiveRecordSchemasQuery(ids), [ids]);
  const variables = useMemo(
    () => ({
      tenantId,
      ...Object.fromEntries(ids.map((id, index) => [`categoryId${index}`, id])),
    }),
    [tenantId, ids],
  );
  const result = useSuspenseQuery(query, ids.length > 0 ? { variables } : skipToken);
  const schemas = useMemo<EffectiveRecordPropertySchemasByCategory>(
    () => (ids.length > 0 ? readEffectiveRecordSchemas(ids, result.data ?? {}) : new Map()),
    [ids, result.data],
  );
  const refetch = useCallback(
    async (nextCategoryIDs: readonly string[] = ids) => {
      const requested = [...new Set(nextCategoryIDs)].sort();
      if (requested.length === 0) return;
      const fresh = await client.query({
        query: buildEffectiveRecordSchemasQuery(requested),
        variables: { tenantId, ...Object.fromEntries(requested.map((id, index) => [`categoryId${index}`, id])) },
        fetchPolicy: "network-only",
      });
      readEffectiveRecordSchemas(requested, fresh.data ?? {});
    },
    [client, tenantId, ids],
  );
  return { schemas, refetch };
}
